import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiMapPin, FiClock, FiSend, FiUser, FiMessageSquare, FiCheckCircle, FiAlertCircle, FiPhone } from 'react-icons/fi';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useGSAPScrollAnimations from '../hooks/useGSAPScrollAnimations';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { createTicketFromContactForm } from '../utils/hubspot';

// Test function for backend API - remove after testing
window.testBackendAPI = async () => {
  console.log('🔍 Testing Backend API...');
  
  try {
    // Test health endpoint
    const healthResponse = await fetch('http://localhost:5000/api/health');
    const healthData = await healthResponse.json();
    console.log('✅ Backend API Health:', healthData);
    
    // Test contact form endpoint
    const testData = {
      name: 'Test User',
      email: 'test@bervin.dev',
      subject: 'API Connection Test',
      message: 'This is a test message to verify the backend API integration works correctly.'
    };
    
    const result = await createTicketFromContactForm(testData);
    if (result.success) {
      console.log('✅ Contact form API test successful!');
      console.log('✅ Ticket ID:', result.ticketId);
      console.log('📧 Check your HubSpot account for the test ticket');
      return result;
    } else {
      console.log('❌ Contact form API test failed:', result.message);
      return result;
    }
  } catch (error) {
    console.log('❌ Error testing backend API:', error.message);
    return { success: false, error: error.message };
  }
};

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const formRef = useRef(null);
  const infoRef = useRef(null);
  const animationsCompleted = useRef(false); // Track animations completion
  
  const { /* fadeInUp, fadeInLeft, fadeInRight, */ staggerIn, parallax } = useGSAPScrollAnimations();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [ticketId, setTicketId] = useState(null);

  useEffect(() => {
    // Only run animations once on mount, not on every re-render
    if (animationsCompleted.current) return;
    
    // Hero section animation
    if (heroRef.current) {
      const heroElements = heroRef.current.children;
      staggerIn(Array.from(heroElements), { 
        stagger: 0.2,
        y: 60,
        delay: 0.3 
      });
    }
    
    // Form animation - removed to prevent slide animation on typing
    // if (formRef.current) {
    //   fadeInLeft(formRef.current, { delay: 0.4 });
    // }
    
    // Info cards animation
    if (infoRef.current) {
      const infoCards = infoRef.current.children;
      staggerIn(Array.from(infoCards), { 
        stagger: 0.15,
        y: 50,
        delay: 0.6 
      });
    }

    // Parallax background elements
    const backgroundElements = sectionRef.current?.querySelectorAll('.parallax-bg');
    if (backgroundElements) {
      backgroundElements.forEach((element, index) => {
        parallax(element, { 
          yPercent: index % 2 === 0 ? -30 : 30,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        });
      });
    }
    
    animationsCompleted.current = true;
  }, []); // Empty dependency array - only run once on mount

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setTicketId(null);

    try {
      // Create HubSpot ticket
      const result = await createTicketFromContactForm(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setTicketId(result.ticketId);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        console.error('HubSpot ticket creation failed:', result.error);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FiMail,
      title: "Email Us",
      description: "Get in touch via email",
      contact: "bervin.helpdesk@gmail.com",
      link: "mailto:bervin.helpdesk@gmail.com",
      gradient: "from-secondary-500 to-secondary-600"
    },
    {
      icon: FiPhone,
      title: "Call Us",
      description: "Speak directly with our team",
      contact: "+91 7448378077",
      link: "tel:+917448378077",
      gradient: "from-green-500 to-green-600"
    },
    {
      icon: FiMapPin,
      title: "Location",
      description: "Our headquarters",
      contact: "Bangalore, India",
      link: null,
      gradient: "from-accent-500 to-accent-600"
    },
    {
      icon: FiClock,
      title: "Response Time",
      description: "We typically respond within",
      contact: "24-48 hours",
      link: null,
      gradient: "from-primary-500 to-primary-600"
    }
  ];

  const faqs = [
    {
      question: "How can I get started with Bervin?",
      answer: "Getting started is easy! Simply sign up for an account and you'll have access to our unified AI platform with various tools and capabilities."
    },
    {
      question: "Do you offer enterprise solutions?",
      answer: "Yes, we offer enterprise-grade solutions with advanced features, dedicated support, and custom integrations tailored to your organization's needs."
    },
    {
      question: "What AI tools are available on the platform?",
      answer: "Our platform integrates various AI tools including text generation, image processing, data analysis, and more. We're constantly adding new tools and capabilities."
    },
    {
      question: "Is there technical support available?",
      answer: "Absolutely! We provide comprehensive technical support through multiple channels including email, documentation, and community forums."
    }
  ];

  return (
    <div className="min-h-screen bg-dark-900">
      <Header />
      
      <main ref={sectionRef} className="relative overflow-hidden">
        {/* Background Elements with Parallax */}
        <div className="absolute inset-0 opacity-30">
          <div className="parallax-bg absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"></div>
          <div className="parallax-bg absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-3xl"></div>
        </div>

        {/* Hero Section */}
        <section className="py-24 px-6 relative z-10">
          <div className="container mx-auto">
            <div ref={heroRef} className="text-center mb-16">
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
                <span className="gradient-text">Get in Touch</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Have questions about Bervin? Want to explore partnerships? We'd love to hear from you.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-12 px-6 relative z-10">
          <div className="container mx-auto">
            <div ref={infoRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative"
                >
                  {/* Card */}
                  <div className="relative h-full p-8 glass border border-white/10 rounded-2xl overflow-hidden hover-lift text-center">
                    {/* Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${info.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    {/* Icon Container */}
                    <div className={`relative w-16 h-16 bg-gradient-to-r ${info.gradient} rounded-xl flex items-center justify-center mb-6 mx-auto shadow-lg`}>
                      <info.icon className="w-8 h-8 text-white" />
                      
                      {/* Glow Effect */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${info.gradient} rounded-xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300`}></div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {info.title}
                      </h3>
                      <p className="text-gray-300 mb-4">
                        {info.description}
                      </p>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-accent-400 hover:text-accent-300 font-medium transition-colors duration-300"
                        >
                          {info.contact}
                        </a>
                      ) : (
                        <span className="text-accent-400 font-medium">
                          {info.contact}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & FAQ */}
        <section className="py-20 px-6 relative z-10">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div ref={formRef}>
                <div className="relative p-8 glass border border-white/10 rounded-3xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/5 to-accent-500/5 rounded-3xl"></div>
                  
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-display font-bold mb-8">
                      <span className="gradient-text">Send us a Message</span>
                    </h2>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name and Email Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                            Full Name
                          </label>
                          <div className="relative">
                            <FiUser className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-accent-500/50"
                              placeholder="Enter your name"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                            Email Address
                          </label>
                          <div className="relative">
                            <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-accent-500/50"
                              placeholder="Enter your email"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Phone and Subject Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                            Phone Number
                          </label>
                          <div className="relative">
                            <FiPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-accent-500/50"
                              placeholder="Enter your phone number"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                            Subject
                          </label>
                          <input
                            type="text"
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-accent-500/50"
                            placeholder="What's this about?"
                          />
                        </div>
                      </div>
                      
                      {/* Message Field */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                          Message
                        </label>
                        <div className="relative">
                          <FiMessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                          <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            rows={6}
                            className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-accent-500/50 resize-none"
                            placeholder="Tell us more about your inquiry..."
                          />
                        </div>
                      </div>
                      
                      {submitStatus && (
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`p-6 rounded-xl border ${
                            submitStatus === 'success' 
                              ? 'bg-green-500/10 border-green-500/20 text-green-400' 
                              : 'bg-red-500/10 border-red-500/20 text-red-400'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {submitStatus === 'success' ? (
                              <FiCheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            ) : (
                              <FiAlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <p className="font-medium mb-2">
                                {submitStatus === 'success' 
                                  ? 'Message sent successfully!' 
                                  : 'Failed to send message.'}
                              </p>
                              {submitStatus === 'success' ? (
                                <div className="text-sm opacity-80">
                                  <p>We've received your inquiry and created a support ticket in our HubSpot system.</p>
                                  {ticketId && (
                                    <p className="mt-1">
                                      <span className="font-medium">HubSpot Ticket ID:</span> {ticketId}
                                    </p>
                                  )}
                                  <p className="mt-2">Our team will get back to you within 24-48 hours.</p>
                                </div>
                              ) : (
                                <p className="text-sm opacity-80">
                                  Please try again or contact us directly at bervin.helpdesk@gmail.com
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                      
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                        whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                        className={`w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <FiSend className="w-5 h-5" />
                            Send Message
                          </>
                        )}
                      </motion.button>
                    </form>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-8">
                  <span className="gradient-text">Frequently Asked Questions</span>
                </h2>
                
                <div className="space-y-6">
                  {faqs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="group"
                    >
                      <div className="relative p-6 glass border border-white/10 rounded-2xl hover-lift">
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/5 to-accent-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                        
                        <div className="relative z-10">
                          <h3 className="text-lg font-bold text-white mb-3 group-hover:text-accent-400 transition-colors duration-300">
                            {faq.question}
                          </h3>
                          <p className="text-gray-300 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Additional Contact Info */}
                <div className="mt-12 p-8 bg-gradient-to-br from-dark-800/90 to-dark-700/90 backdrop-blur-sm border border-white/10 rounded-3xl">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Still have questions?
                  </h3>
                  <p className="text-gray-300 mb-8 text-lg">
                    Can't find the answer you're looking for? Our team is here to help.
                  </p>
                  <motion.a
                    href="mailto:bervin.helpdesk@gmail.com"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 rounded-xl text-white font-semibold text-lg hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300"
                  >
                    <FiMail className="w-5 h-5" />
                    Email Support
                  </motion.a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
